// Mirrors server/src/auth/{permissions,principal}.ts and the JSON returned by
// the FC. Keep the two sides in sync by hand; the server is the authority.

export type Role = "office" | "chair" | "proctor" | "tech";
export type Group = "youth" | "adult";

export type ModuleId =
  | "overview"
  | "candidates"
  | "roster"
  | "papers"
  | "scores"
  | "answer-sheets"
  | "exports"
  | "proctor"
  | "admins"
  | "system";

export type Permission =
  | "candidate:read"
  | "candidate:disable"
  | "candidate:enable"
  | "roster:import"
  | "exam:manage"
  | "score:read"
  | "export:run"
  | "proctor:read"
  | "admin:read"
  | "admin:write";

export type ScopeList<T> = "all" | T[];

export interface Scope {
  groups: ScopeList<Group>;
  regions: ScopeList<number>;
  sites: ScopeList<number>;
}

export interface Principal {
  username: string;
  displayName: string;
  role: Role;
  scope: Scope;
  modules: ModuleId[];
  permissions: Permission[];
}

export interface SessionResponse {
  serverTime: string;
  expiresAt: string;
  principal: Principal;
}

export interface LoginResponse extends SessionResponse {
  sessionToken: string;
}

export interface CatalogGroup {
  id: Group;
  name: string;
  exam: { id: string; title: string; opensAt: string; contentHash: string; totalScore: number } | null;
}

export interface CatalogResponse {
  groups: CatalogGroup[];
  regions: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string; regionId: number }>;
  rooms: Array<{ id: number; name: string; siteId: number }>;
}

export const ROLE_NAMES: Record<Role, string> = {
  office: "认证办公室",
  chair: "省/区域主席",
  proctor: "监考",
  tech: "技术团队"
};

export const GROUP_NAMES: Record<Group, string> = {
  youth: "青少年组",
  adult: "成人组"
};

export interface ScoreSummary {
  total: number;
  totalScore: number;
  answered: number;
  questionCount: number;
}

export interface CandidateSummary {
  username: string;
  name: string;
  province: string | null;
  group: Group;
  regionId: number;
  siteId: number;
  roomId: number | null;
  seat: string | null;
  examKey: string;
  enabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  lastSubmitAt: string | null;
  lastSubmitIp: string | null;
}

export interface CandidateListRequest {
  group?: Group;
  regionId?: number;
  siteId?: number;
  roomId?: number;
  enabled?: boolean;
  keyword?: string;
  /** Sort by admission number; ascending by default. */
  order?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface CandidateListResponse {
  total: number;
  page: number;
  pageSize: number;
  items: CandidateSummary[];
}

export interface CandidateDetail extends CandidateSummary {
  password: string;
  exam: { id: string; title: string; contentHash: string; totalScore: number } | null;
  examMismatch: boolean;
  enabledHistory: Array<{ at: string; enabled: boolean }>;
  loginHistory: Array<{ at: string; ip: string }>;
  submitHistory: Array<{ at: string; ip: string }>;
}

export interface SetEnabledResponse {
  requested: number;
  updated: number;
  failed: string[];
}

export interface SetEnabledBySiteResponse {
  matched: number;
  excluded: string[];
  unchanged: number;
  updated: number;
  failed: string[];
}

export interface SiteStats {
  siteId: number;
  regionId: number;
  roomId?: number;
  group: Group;
  total: number;
  enabled: number;
  loggedIn: number;
  submitted: number;
}

export interface OverviewResponse {
  generatedAt: string;
  sites: SiteStats[];
  rooms: SiteStats[];
}

export interface RosterProblem {
  line: number;
  message: string;
}

export interface RosterSummary {
  rowCount: number;
  newRegions: string[];
  newSites: string[];
  newRooms: number;
}

export type RosterImportResponse =
  | ({ ok: false; problems: RosterProblem[]; problemCount: number } & RosterSummary)
  | ({ ok: true; dryRun: true; problems: [] } & RosterSummary)
  | ({ ok: true; dryRun: false; imported: number; failed: string[] } & RosterSummary);

export interface ScoreReportCandidate {
  username: string;
  name: string;
  regionId: number;
  siteId: number;
  enabled: boolean;
  answered: number;
  total: number;
  groupRank: number;
  siteRank: number;
}

export interface ScoreReport {
  exam: { id: string; title: string; totalScore: number; questionCount: number };
  computedAt: string;
  summary: { candidates: number; participants: number; average: number | null; max: number | null; min: number | null };
  distribution: Array<{ from: number; to: number; count: number }>;
  sites: Array<{ siteId: number; regionId: number; total: number; participants: number; average: number | null; max: number | null }>;
  questions: Array<{ id: string; score: number; correct: number; rate: number | null }>;
  candidates: ScoreReportCandidate[];
}

export interface AdminItem {
  username: string;
  displayName: string;
  role: Role;
  scope: Scope;
  enabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
}

export interface AdminSaveRequest {
  username: string;
  displayName: string;
  role: Role;
  groups: ScopeList<Group>;
  regions: ScopeList<number>;
  sites: ScopeList<number>;
  enabled: boolean;
  password?: string;
}

export interface AdminSaveResponse {
  created: boolean;
  passwordChanged: boolean;
  admin: AdminItem | null;
}

export interface AnswerSheetResponse {
  username: string;
  name: string;
  filename: string;
  html: string;
}

export interface GeneratedProctor {
  username: string;
  password: string;
  siteId: number;
  siteName: string;
  regionName: string;
}

export interface ProctorCandidate {
  username: string;
  name: string;
  group: Group;
  roomId: number | null;
  seat: string | null;
  enabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  lastSubmitAt: string | null;
  lastSubmitIp: string | null;
  answered: number;
}

export interface ProctorSiteResponse {
  generatedAt: string;
  site: { id: number; name: string; regionId: number; regionName: string | null };
  rooms: Array<{ id: number; name: string }>;
  summary: { total: number; loggedIn: number; submitted: number; disabled: number };
  candidates: ProctorCandidate[];
}

export type ExamGroupId = Group | "simulation";

export const EXAM_GROUP_NAMES: Record<ExamGroupId, string> = {
  youth: "青少年组",
  adult: "成人组",
  simulation: "模拟练习"
};

export interface PaperItem {
  examKey: string;
  id: string;
  title: string;
  group: Group | null;
  opensAt: string;
  contentHash: string;
  questionCount: number;
  totalScore: number;
  createdAt: string;
  createdBy: string;
  /** Groups whose current paper is this version. */
  currentFor: ExamGroupId[];
  boundCandidates: number;
}

export interface PapersListResponse {
  items: PaperItem[];
  bindings: Partial<Record<ExamGroupId, { examKey: string; updatedAt: string; updatedBy: string }>>;
  /** Candidate counts per group, for the rebind confirmation. */
  candidates: Record<string, number>;
}

export interface PaperPublishRequest {
  id: string;
  group?: Group;
  opensAt: string;
  defaultScore: number;
  source: string;
  dryRun: boolean;
}

export interface PaperBuildSummary {
  examKey: string;
  id: string;
  title: string;
  questionCount: number;
  totalScore: number;
  contentHash: string;
  exists: boolean;
}

export type PaperPublishResponse =
  | { ok: false; message: string }
  | ({ ok: true; dryRun: true } & PaperBuildSummary)
  | ({ ok: true; dryRun: false; created: boolean } & PaperBuildSummary);

export interface PaperPreviewResponse {
  examKey: string;
  title: string;
  html: string;
}

export interface SetCurrentPaperResponse {
  groupId: ExamGroupId;
  examKey: string;
  rebound: number;
  failures: string[];
}
